import { z } from 'zod';

export const ProblemDetailsSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string(),
  instance: z.string(),
  request_id: z.string().optional(),
});

export const ValidationProblemDetailsSchema = ProblemDetailsSchema.extend({
  invalid_params: z.array(
    z.object({
      name: z.string(),
      reason: z.string(),
    }),
  ),
});

const DEFAULT_BASE_URI = 'https://api.shiftagent.com/errors';

let BASE_URI = DEFAULT_BASE_URI;

/**
 * Configure the base URI used to build problem `type` URLs (RFC 9457).
 * Call at config-load time with e.g. `${apiPublicUrl}/problems`.
 * Passing undefined (or an empty string) resets to the built-in default.
 */
export function configureProblemBaseUri(uri: string | undefined): void {
  BASE_URI = uri ? uri.replace(/\/+$/, '') : DEFAULT_BASE_URI;
}

export function getProblemBaseUri(): string {
  return BASE_URI;
}

export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;

export function createProblemDetails(opts: {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
  request_id?: string | undefined;
  extensions?: Record<string, unknown> | undefined;
}): ProblemDetails & Record<string, unknown> {
  const { extensions, ...base } = opts;
  const typeUri = base.type.startsWith('https://') ? base.type : `${BASE_URI}/${base.type}`;
  return {
    ...base,
    type: typeUri,
    ...extensions,
  };
}

function helper(
  type: string,
  title: string,
  status: number,
  detail: string,
  instance: string,
  request_id?: string,
): ProblemDetails {
  return createProblemDetails({ type, title, status, detail, instance, request_id });
}

export const notFound = (detail: string, instance: string, request_id?: string) =>
  helper('not_found', 'Not Found', 404, detail, instance, request_id);

export const badRequest = (detail: string, instance: string, request_id?: string) =>
  helper('bad_request', 'Bad Request', 400, detail, instance, request_id);

export const unauthorized = (detail: string, instance: string, request_id?: string) =>
  helper('unauthorized', 'Unauthorized', 401, detail, instance, request_id);

export const forbidden = (detail: string, instance: string, request_id?: string) =>
  helper('forbidden', 'Forbidden', 403, detail, instance, request_id);

export const conflict = (detail: string, instance: string, request_id?: string) =>
  helper('conflict', 'Conflict', 409, detail, instance, request_id);

export const unprocessableEntity = (detail: string, instance: string, request_id?: string) =>
  helper('unprocessable_entity', 'Unprocessable Entity', 422, detail, instance, request_id);

export const tooManyRequests = (detail: string, instance: string, request_id?: string) =>
  helper('too_many_requests', 'Too Many Requests', 429, detail, instance, request_id);

export const internalServerError = (detail: string, instance: string, request_id?: string) =>
  helper('internal_server_error', 'Internal Server Error', 500, detail, instance, request_id);

// ---------------------------------------------------------------------------
// Integration API contract problem registry (kebab-case slugs).
// The snake_case helpers above remain untouched for legacy callers; new
// contract-facing routes use `contractProblem()` with these slugs.
// ---------------------------------------------------------------------------

export type ContractProblemSlug =
  | 'validation-error'
  | 'not-found'
  | 'name-conflict'
  | 'external-id-conflict'
  | 'cross-tenant'
  | 'conversation-archived'
  | 'resource-in-use'
  | 'role-required'
  | 'tenant-suspended'
  | 'insufficient-scope'
  | 'idempotency-key-conflict'
  | 'missing-secret'
  | 'capacity-exhausted'
  | 'rate-limited';

export const CONTRACT_PROBLEM_TITLES: Record<ContractProblemSlug, string> = {
  'validation-error': 'Validation error',
  'not-found': 'Not found',
  'name-conflict': 'Name conflict',
  'external-id-conflict': 'External ID conflict',
  'cross-tenant': 'Cross-tenant reference',
  'conversation-archived': 'Conversation archived',
  'resource-in-use': 'Resource in use',
  'role-required': 'Role required',
  'tenant-suspended': 'Tenant suspended',
  'insufficient-scope': 'Insufficient scope',
  'idempotency-key-conflict': 'Idempotency key conflict',
  'missing-secret': 'Missing secret',
  'capacity-exhausted': 'Capacity exhausted',
  'rate-limited': 'Rate limited',
};

/**
 * Well-known extension members carried by contract problems. Arbitrary extra
 * extension members pass through unchanged.
 */
export interface ContractProblemExtensions extends Record<string, unknown> {
  conflicting_resource_id?: string;
  errors?: Array<{ pointer: string; message: string }>;
  retry_after_seconds?: number;
}

export interface ContractProblem extends Record<string, unknown> {
  type: string;
  title: string;
  status: number;
  detail: string;
  request_id?: string;
  instance?: string;
}

/**
 * Build an RFC 9457 problem body for an Integration API contract slug:
 * `{ type: ${baseUri}/${slug}, title, status, detail, request_id?, instance?, ...extensions }`.
 */
export function contractProblem(
  slug: ContractProblemSlug,
  status: number,
  detail: string,
  opts?: {
    request_id?: string | undefined;
    instance?: string | undefined;
    extensions?: ContractProblemExtensions | undefined;
  },
): ContractProblem {
  const body: ContractProblem = {
    type: `${BASE_URI}/${slug}`,
    title: CONTRACT_PROBLEM_TITLES[slug],
    status,
    detail,
  };
  if (opts?.request_id !== undefined) body.request_id = opts.request_id;
  if (opts?.instance !== undefined) body.instance = opts.instance;
  if (opts?.extensions) Object.assign(body, opts.extensions);
  return body;
}
